

import React, { useState } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewPricingCategoryModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        pricing_type: 'Dated',
        currency: 'GBP',
        gross_cost: '',
        solo_cost: '',
        min_qty: '',
        max_qty: '',
        date_from: '',
        date_to: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.pricing_type) errors.pricing_type = 'Pricing Type is required';
        if (!formData.currency) errors.currency = 'Currency is required';
        if (!formData.date_from) errors.date_from = 'Date From is required';
        if (!formData.date_to) errors.date_to = 'Date To is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                gross_cost: formData.gross_cost ? parseFloat(formData.gross_cost) : null,
                solo_cost: formData.solo_cost ? parseFloat(formData.solo_cost) : null,
                min_qty: formData.min_qty ? parseInt(formData.min_qty) : null,
                max_qty: formData.max_qty ? parseInt(formData.max_qty) : null
            };
            await onSave(payload);
            setFormData({
                name: '',
                pricing_type: 'Dated',
                currency: 'GBP',
                gross_cost: '',
                solo_cost: '',
                min_qty: '',
                max_qty: '',
                date_from: '',
                date_to: ''
            });
            onClose();
        } catch (error) {
            console.error('Error saving pricing category:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving pricing category',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '80%', md: '700px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <MDBox className="pearl-popup-header"
                        sx={{
                            backgroundColor: colors.popupHeader.background,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8
                        }}
                    >
                        <MDTypography variant="h6" sx={{ color: '#FFFFFF' }}>
                            New Pricing Category
                        </MDTypography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon sx={{ color: '#FFFFFF' }} />
                        </IconButton>
                    </MDBox>
                    <MDBox p={3}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Pricing Type</InputLabel>
                                        <Select
                                            name="pricing_type"
                                            value={formData.pricing_type}
                                            onChange={handleInputChange}
                                            label="Pricing Type"

                                        >
                                            <MenuItem value="Dated">Dated</MenuItem>
                                            <MenuItem value="Tiered">Tiered</MenuItem>
                                            <MenuItem value="Per Pax">Per Pax</MenuItem>
                                            <MenuItem value="Per Room">Per Room</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            label="Currency"

                                        >
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="AUD">AUD</MenuItem>
                                            <MenuItem value="EUR">EUR</MenuItem>
                                            <MenuItem value="USD">USD</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Gross Cost"
                                        name="gross_cost"
                                        type="number"
                                        value={formData.gross_cost}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Solo Cost"
                                        name="solo_cost"
                                        type="number"
                                        value={formData.solo_cost}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Min Qty"
                                        name="min_qty"
                                        type="number"
                                        value={formData.min_qty}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Max Qty"
                                        name="max_qty"
                                        type="number"
                                        value={formData.max_qty}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date From"
                                        name="date_from"
                                        type="date"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.date_from}
                                        onChange={handleInputChange}
                                        error={!!formErrors.date_from}
                                        helperText={formErrors.date_from}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date To"
                                        name="date_to"
                                        type="date"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.date_to}
                                        onChange={handleInputChange}
                                        error={!!formErrors.date_to}
                                        helperText={formErrors.date_to}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                            <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <MDButton variant="outlined" color="secondary" onClick={onClose}>
                                    Cancel
                                </MDButton>
                                <MDButton variant="gradient" color="info" type="submit" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </MDBox>
                </Box>
            </Modal>
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity}>{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default NewPricingCategoryModal;

